import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ccmApi } from '../lib/api';
import {
  modeLabel, formatIsoDate, nextPollDelay, POLL_BUDGET_MS, prefsBody, newClientRequestId,
} from '../lib/note-expander-v2';
import {
  MAX_MODELS, PROMPTS, PROMPT_STYLE_WORDS, toggleModel, playBody, comparisonSettled, columnFacts, columnState, promoteError,
  REASONING_CAPTION, reasoningEntryTitle,
} from '../lib/note-expander-v2-playground';
import { NoteExpanderV2Review } from './note-expander-v2-review';

// Note Expander v2, the Playground (PR 5b, O192; DESIGN §7.5): one of your drafts through up to
// four models side by side. Each column is an ordinary pipeline run, every safety check included,
// and nothing here reaches the chart: "Use this one" turns the column into an ordinary run of the
// draft (by copy, no new model call) and opens the normal review, where you approve and write.
// The models offered are exactly GET /v2/models; the server refuses anything else.

const PROVIDER = { bedrock: 'Bedrock', openai: 'OpenAI' };

export function NoteExpanderV2Playground({ copy, view, models, onSaved, onPromoted }) {
  const entries = (models && models.models) || [];
  const byAlias = useMemo(() => new Map(entries.map((m) => [m.alias, m])), [entries]);
  const [drafts, setDrafts] = useState(null);
  const [draftId, setDraftId] = useState('');
  const [mode, setMode] = useState(view.settings.defaultMode);
  const [prompt, setPrompt] = useState('standard');
  const [picked, setPicked] = useState(() => [view.model.alias]);
  const [requestKey, setRequestKey] = useState(() => newClientRequestId());
  const [comparison, setComparison] = useState(null); // { comparisonId, columns, stale }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [columnError, setColumnError] = useState({}); // runId → words
  const [savedDefault, setSavedDefault] = useState('');
  const [reviewing, setReviewing] = useState(null);
  const poll = useRef({ timer: null, live: true });

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        let r = await ccmApi.noteExpanderInbox({ scope: 'mine' });
        if (!r.mineAvailable) r = await ccmApi.noteExpanderInbox({ scope: 'all' });
        if (live) setDrafts((r.drafts || []).filter((d) => d.classification && (d.classification.expandable || d.classification.kind === 'expanded')));
      } catch (_) {
        if (live) setDrafts([]);
      }
    })();
    return () => { live = false; };
  }, []);
  useEffect(() => () => { poll.current.live = false; clearTimeout(poll.current.timer); }, []);

  // A changed form is a new comparison; the same form pressed again is the same one (no second
  // set of model calls), until "Run again".
  useEffect(() => { setRequestKey(newClientRequestId()); }, [draftId, mode, prompt, picked.join(',')]);

  const hasStyle = !!view.profile;
  const chosenDraft = drafts && drafts.find((d) => String(d.draftId) === String(draftId));

  function follow(comparisonId) {
    clearTimeout(poll.current.timer);
    const since = Date.now();
    let attempt = 0;
    const tick = async () => {
      if (!poll.current.live) return;
      if (Date.now() - since > POLL_BUDGET_MS) { setComparison((c) => (c ? { ...c, stale: true } : c)); return; }
      try {
        const r = await ccmApi.ne2Comparison(comparisonId);
        if (!poll.current.live) return;
        setComparison({ comparisonId, columns: r.columns || [], stale: false });
        if (comparisonSettled(r.columns)) return;
      } catch (_) { /* retried on the next tick */ }
      attempt += 1;
      poll.current.timer = setTimeout(tick, nextPollDelay(attempt));
    };
    poll.current.timer = setTimeout(tick, nextPollDelay(0));
  }

  async function run(fresh) {
    const key = fresh ? newClientRequestId() : requestKey;
    if (fresh) setRequestKey(key);
    setBusy(true); setError(''); setColumnError({}); setSavedDefault('');
    try {
      const r = await ccmApi.ne2Play(playBody({ draftId, mode, prompt, models: picked, clientRequestId: key }));
      setComparison({ comparisonId: r.comparisonId, columns: (r.runs || []).map((x) => ({ runId: x.id, model: x.model, mode: x.mode, status: x.status, promptStyle: null })), stale: false });
      follow(r.comparisonId);
    } catch (e) {
      const reason = e && e.body && e.body.reason;
      const words = reason && ((copy.playground && copy.playground[reason]) || (copy.request && copy.request[reason]) || (copy.run && copy.run[reason]));
      setError(words || (e && e.status === 400 ? 'Choose a draft, a mode, a prompt and up to four models.' : 'The comparison could not start. Try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function useThis(col) {
    setColumnError((m) => ({ ...m, [col.runId]: '' }));
    try {
      const r = await ccmApi.ne2Promote(col.runId);
      if (onPromoted) onPromoted(String(r.run.draftId));
      setReviewing({
        runId: r.run.id, draftId: String(r.run.draftId),
        patientName: chosenDraft && chosenDraft.patient ? chosenDraft.patient.name : null,
        patientElationId: chosenDraft && chosenDraft.patient ? chosenDraft.patient.elationId : null,
      });
    } catch (e) {
      setColumnError((m) => ({ ...m, [col.runId]: promoteError(e, copy) }));
    }
  }

  async function makeDefault(col) {
    try {
      const next = await ccmApi.ne2SavePrefs(prefsBody({ ...view.settings, defaultModel: col.model }));
      onSaved(next);
      setSavedDefault(col.model);
    } catch (_) {
      setColumnError((m) => ({ ...m, [col.runId]: 'Your default model could not be saved. Try again from Preferences.' }));
    }
  }

  if (reviewing) {
    return (
      <NoteExpanderV2Review runId={reviewing.runId} copy={copy} patientName={reviewing.patientName}
                            patientElationId={reviewing.patientElationId}
                            onClose={() => {
                              // A write or discard changed the draft's row: refresh it, as the inbox's own review does.
                              if (onPromoted) onPromoted(reviewing.draftId);
                              setReviewing(null);
                            }} />
    );
  }

  const canRun = !!draftId && picked.length > 0 && !busy && (prompt === 'standard' || hasStyle);

  return (
    <div className="ne2-play">
      <p className="ne2-prefs-lede">
        Run one of your drafts through up to four models and compare. Every column passes the same safety checks;
        nothing here is written to the chart until you choose one, review it and write it.
      </p>

      <section className="ne2-pref">
        <div className="ne2-pref-head"><h3>Draft</h3></div>
        {drafts == null && <div className="ne-empty">Loading your drafts…</div>}
        {drafts && !drafts.length && <div className="ne-empty">No unsigned drafts to try.</div>}
        {drafts && drafts.length > 0 && (
          <select className="ne2-select" value={draftId} aria-label="Draft to try" onChange={(e) => setDraftId(e.target.value)}>
            <option value="">Choose a draft…</option>
            {drafts.map((d) => (
              <option key={String(d.draftId)} value={String(d.draftId)}>
                {(d.patient && d.patient.name) || 'Patient'}{d.date ? ` · ${String(d.date).slice(0, 10)}` : ''}
              </option>
            ))}
          </select>
        )}
      </section>

      <section className="ne2-pref">
        <div className="ne2-pref-head"><h3>Mode</h3></div>
        <div className="ne2-seg" role="radiogroup" aria-label="Mode">
          {['grounded', 'maximal'].map((m) => (
            <button key={m} type="button" role="radio" aria-checked={mode === m}
                    className={'ne2-seg-btn' + (mode === m ? ' is-on' : '')} onClick={() => setMode(m)}>{modeLabel(m)}</button>
          ))}
        </div>
      </section>

      <section className="ne2-pref">
        <div className="ne2-pref-head">
          <h3>Prompt</h3>
          {!hasStyle && <p>Your style needs an approved style in Preferences first.</p>}
          {prompt === 'compare' && <p>Standard vs your style runs the first model you tick, twice.</p>}
        </div>
        <div className="ne2-seg" role="radiogroup" aria-label="Prompt">
          {PROMPTS.map(([value, label]) => (
            <button key={value} type="button" role="radio" aria-checked={prompt === value} disabled={value !== 'standard' && !hasStyle}
                    className={'ne2-seg-btn' + (prompt === value ? ' is-on' : '')} onClick={() => setPrompt(value)}>{label}</button>
          ))}
        </div>
      </section>

      <section className="ne2-pref">
        <div className="ne2-pref-head">
          <h3>Models</h3>
          <p>Up to {MAX_MODELS}. {models && models.marksNote}</p>
        </div>
        <div className="ne2-play-models">
          {entries.map((m) => {
            const on = picked.includes(m.alias);
            return (
              <label key={m.alias} className="ne2-check">
                <input type="checkbox" checked={on} disabled={!on && picked.length >= MAX_MODELS}
                       onChange={() => setPicked((list) => toggleModel(list, m.alias))} />
                <span>
                  {m.display} ({PROVIDER[m.provider] || m.provider})
                  {(m.marks || []).map((mk) => <span key={mk.id} className="ne2-mark"> · {mk.label}</span>)}
                  {m.retires && <span className="ne2-hint"> · retires {formatIsoDate(m.retires)}</span>}
                </span>
              </label>
            );
          })}
        </div>
      </section>

      <div className="ne2-save">
        <button type="button" className="btn accent" onClick={() => run(false)} disabled={!canRun}>{busy ? 'Starting…' : 'Run'}</button>
        {comparison && <button type="button" className="btn" onClick={() => run(true)} disabled={!canRun}>Run again</button>}
        {error && <span className="ne2-field-error" role="alert">{error}</span>}
      </div>

      {comparison && (
        <div className="ne2-play-grid">
          {comparison.columns.map((col) => (
            <PlaygroundColumn key={col.runId} col={col} entry={byAlias.get(col.model)} copy={copy} error={columnError[col.runId] || ''}
                              isDefault={savedDefault === col.model || view.settings.defaultModel === col.model}
                              onUse={() => useThis(col)} onDefault={() => makeDefault(col)} />
          ))}
        </div>
      )}
      {comparison && comparison.stale && <div className="ne2-hint" role="status">Still running. Come back to this tab later.</div>}
    </div>
  );
}

// The model's reasoning for one column (owner 2026-10-21, Playground only), read when first opened.
export function ColumnReasoning({ runId }) {
  const [entries, setEntries] = useState(null);
  const [failed, setFailed] = useState('');
  const asked = useRef(false);
  async function onToggle(e) {
    if (!e.currentTarget.open || asked.current) return;
    asked.current = true;
    try {
      const r = await ccmApi.ne2RunReasoning(runId);
      setEntries(r && Array.isArray(r.entries) ? r.entries : []);
    } catch (_e) {
      asked.current = false;
      setFailed('The reasoning could not be loaded. Close and open it to try again.');
    }
  }
  return (
    <details className="ne2-play-reasoning" onToggle={onToggle}>
      <summary>Reasoning</summary>
      <div className="ne2-hint">{REASONING_CAPTION}</div>
      {failed && <div className="ne2-field-error" role="alert">{failed}</div>}
      {entries === null && !failed && <div className="ne2-hint" role="status">Loading…</div>}
      {entries && entries.length === 0 && <div className="ne2-hint">Nothing was kept.</div>}
      {entries && entries.map((e, i) => (
        <section key={`${e.stage}-${e.call}-${i}`}>
          <div className="ne2-play-facts">{reasoningEntryTitle(e)}</div>
          <pre className="ne2-play-thought">{e.text}</pre>
        </section>
      ))}
    </details>
  );
}

// One column: the model and prompt, its marks and retirement, its state or why it stopped, the
// facts, the note itself, its reasoning when the model returned any, and the two choices once it
// is ready.
function PlaygroundColumn({ col, entry, copy, error, isDefault, onUse, onDefault }) {
  const state = columnState(col, copy);
  return (
    <article className="ne2-play-col">
      <h4>{entry ? `${entry.display} (${PROVIDER[entry.provider] || entry.provider})` : col.model}</h4>
      <div className="ne2-hint">
        {col.promptStyle ? PROMPT_STYLE_WORDS[col.promptStyle] : ''}
        {entry && (entry.marks || []).map((mk) => ` · ${mk.label}`).join('')}
        {entry && entry.retires ? ` · retires ${formatIsoDate(entry.retires)}` : ''}
      </div>
      {state.words && <div className={'ne2-state ne2-state-' + state.kind} role="status">{state.words}</div>}
      {columnFacts(col).length > 0 && <div className="ne2-play-facts">{columnFacts(col).join(' · ')}</div>}
      {col.renderedText && <div className="ne2-play-note">{col.renderedText}</div>}
      {col.reasoning === 'captured' && <ColumnReasoning runId={col.runId} />}
      {col.reasoning === 'purged' && <div className="ne2-hint">The model's reasoning was removed after 90 days.</div>}
      {error && <div className="ne2-field-error" role="alert">{error}</div>}
      {state.kind === 'ready' && (
        <div className="ne2-review-actions">
          <button type="button" className="btn accent sm" onClick={onUse}>Use this one</button>
          <button type="button" className="btn sm" onClick={onDefault} disabled={isDefault}>{isDefault ? 'Your default' : 'Make my default'}</button>
        </div>
      )}
    </article>
  );
}
